/**
 * Money is integer cents everywhere (docs/02 "Global conventions", ADR-13).
 * The Beta's float-money defects are the reason this module exists and the
 * reason nothing in PropOS may represent an amount as a fractional number.
 *
 * Storage and transport: `bigint`-backed integer cents in the database
 * (`*_cents bigint`), `number` in TypeScript. A JS safe integer covers
 * ±$90 trillion in cents, well beyond any figure this system will hold.
 *
 * Rendering happens at the edge (docs/03 §5: "money renders from cents, edits
 * in dollars"). Parsing user input is the only place a decimal string appears.
 */

/** A whole number of cents. Never fractional, may be negative (credits). */
export type Cents = number;

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MoneyError';
  }
}

/** Narrows an arbitrary number to Cents, rejecting anything non-integral. */
export function assertCents(value: number): Cents {
  if (!Number.isInteger(value)) {
    throw new MoneyError(`Amount must be integer cents, received ${String(value)}`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new MoneyError(`Amount exceeds safe integer range: ${String(value)}`);
  }
  return value;
}

/**
 * Parses a user-entered dollar amount into cents.
 *
 * Accepts an optional leading currency symbol, thousands separators, an
 * optional sign, and at most two decimal places. A third decimal place is an
 * error rather than a silent round — a typo in a rent figure should stop the
 * save, not quietly change the number.
 */
export function parseDollarsToCents(input: string): Cents {
  const trimmed = input.trim();
  if (trimmed === '') throw new MoneyError('Amount is required');

  const cleaned = trimmed.replace(/[$\s,]/g, '');
  const match = /^(-|\+)?(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) {
    throw new MoneyError(`Enter a dollar amount with at most two decimal places: "${input}"`);
  }

  const [, sign, whole, fraction = ''] = match;
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return assertCents(sign === '-' ? -cents : cents);
}

/** Formats cents as US currency for display, e.g. `-123456` → `-$1,234.56`. */
export function formatCents(cents: Cents, options: { showCents?: boolean } = {}): string {
  assertCents(cents);
  const showCents = options.showCents ?? true;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(cents / 100);
}

/** Formats cents as a bare decimal string for form inputs, e.g. `1234.56`. */
export function centsToInputvalue(cents: Cents): string {
  assertCents(cents);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const fraction = String(abs % 100).padStart(2, '0');
  return `${negative ? '-' : ''}${String(whole)}.${fraction}`;
}

/** Sums cents with an integrality guard on every term and on the total. */
export function sumCents(amounts: readonly Cents[]): Cents {
  return assertCents(amounts.reduce((total, amount) => total + assertCents(amount), 0));
}
